import React, { useEffect, useState } from 'react';
import { useAppContext } from 'context/AppContext';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { getBooking } from 'actions/admin/bookings';
import { getShowing, createShowing, updateShowing, deleteShowing } from 'actions/admin/showings';
import { handleResponse } from 'actions/actionHelpers';
import { convertToNumber } from 'helpers/StringHelper';
import routes from 'routes';
import { required, emailOptional } from 'form/validation';
import { formatUtcIsoDateTime, formatShortDateTime, parseDateTime } from 'helpers/DateHelper';
import strings from 'localization/strings';

import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminPage from '../../presentational/AdminPage';
import Button from '@material-ui/core/Button';
import Loader from 'common/Loader';
import { Link } from 'react-router-dom';
import Form from 'form/Form';
import { TextField, showErrorOnBlur } from 'mui-rff';
import ReadOnlyField from '../../presentational/ReadOnlyField';
import { OnBlur } from 'react-final-form-listeners';

const Showing = () => {
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [showing, setShowing] = useState(undefined);
    const [booking, setBooking] = useState(undefined);
    const [isLoadingBooking, setIsLoadingBooking] = useState(true);
    const [isLoadingShowing, setIsLoadingShowing] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState(undefined);
    const { bookingId, showingId } = params;

    useEffect(() => {
        if(convertToNumber(showingId)) {
            loadShowing();
        } else {
            setShowing({
                startTime: null,
                endTime: null,
                lockSystemUserIdentifier: null
            });
            setIsLoadingShowing(false);
        }
    }, [showingId]);

    useEffect(() => {
        loadBooking();
    }, [bookingId]);

    useEffect(() => {
        if(showing) {
            setInitialValues({
                startTime: formatShortDateTime(showing.startTime, appContext),
                endTime: formatShortDateTime(showing.endTime, appContext),
                lockSystemUserIdentifier: showing.lockSystemUserIdentifier
            });
        }
    }, [showing]);

    const loadBooking = () => {
        setIsLoadingBooking(true);
        dispatch(getBooking(bookingId))
            .then(handleResponse(
                response => {
                    setBooking(response.payload);
                    setIsLoadingBooking(false);
                },
                () => setIsLoadingBooking(false)
            ));
    };

    const loadShowing = () => {
        setIsLoadingShowing(true);
        dispatch(getShowing(showingId))
            .then(handleResponse(
                response => {
                    setShowing(response.payload);
                    setIsLoadingShowing(false);
                },
                () => setIsLoadingShowing(false)
            ));
    };

    const sanitizeDateTime = (form, fieldName) => {
        const formValues = form.getState().values;
        form.change(fieldName, formatShortDateTime(parseDateTime(formValues[fieldName], appContext), appContext));
    };

    const handleFormSubmit = formValues => {
        setIsSubmitting(true);
        const request = {
            bookingId,
            startTime: formatUtcIsoDateTime(parseDateTime(formValues.startTime, appContext)),
            endTime: formatUtcIsoDateTime(parseDateTime(formValues.endTime, appContext)),
            lockSystemUserIdentifier: formValues.lockSystemUserIdentifier
        };
        const action = convertToNumber(showingId)
            ? updateShowing(showingId, request)
            : createShowing(request);
        dispatch(action)
            .then(handleResponse(
                () => {
                    history.push(routes.admin.booking.replace(':bookingId', bookingId));
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Showing was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleDelete = () => {
        if(!convertToNumber(showingId)) {
            return;
        }
        setIsSubmitting(true);
        dispatch(deleteShowing(showingId))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.booking.replace(':bookingId', bookingId));
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Showing was successfully deleted.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const title = `Showing: booking ${bookingId}`;

    return (
        <AdminPage title={title}>
            {
                (isLoadingBooking || isLoadingShowing) && <Loader />
            }
            {
                !isLoadingBooking && booking && !isLoadingShowing && showing && (
                    <AdminContentWrapper type="detail">
                        <AdminFormWrapper isSubmitting={isSubmitting}>
                            <Form
                                initialValues={initialValues}
                                onSubmit={handleFormSubmit}
                            >
                                {({ form, handleSubmit, invalid }) => (
                                    <form onSubmit={handleSubmit}>
                                        <AdminFormFields>
                                            <ReadOnlyField label="Storage site">
                                                {booking.storageSiteTitle}
                                            </ReadOnlyField>

                                            <ReadOnlyField label="Owner">
                                                {booking.ownerName}
                                            </ReadOnlyField>

                                            <ReadOnlyField label="Tenant">
                                                {booking.tenantName}
                                            </ReadOnlyField>

                                            <TextField
                                                name="startTime"
                                                label="Start time"
                                                variant="outlined"
                                                required
                                                fieldProps={{ validate: required }}
                                                placeholder={strings.shortDateTimeHelperText}
                                            />
                                            <OnBlur name="startTime">
                                                {() => sanitizeDateTime(form, 'startTime')}
                                            </OnBlur>

                                            <TextField
                                                name="endTime"
                                                label="End time"
                                                variant="outlined"
                                                required
                                                fieldProps={{ validate: required }}
                                                placeholder={strings.shortDateTimeHelperText}
                                            />
                                            <OnBlur name="endTime">
                                                {() => sanitizeDateTime(form, 'endTime')}
                                            </OnBlur>

                                            <TextField
                                                name="lockSystemUserIdentifier"
                                                label="Email address for lock system"
                                                variant="outlined"
                                                showError={showErrorOnBlur}
                                                fieldProps={{ validate: emailOptional }}
                                                helperText={<>Email address for lock system. Leave empty to use the tenant's email address <em>{booking.tenantEmail}</em>.</>}
                                            />
                                        </AdminFormFields>
                                        <AdminFormButtons>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={invalid}
                                            >
                                                Save
                                            </Button>
                                            {
                                                convertToNumber(showingId) &&
                                                (
                                                    <Button
                                                        type="button"
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={handleDelete}
                                                    >
                                                        Delete
                                                    </Button>
                                                )
                                            }
                                            <Button
                                                component={Link}
                                                to={routes.admin.booking.replace(':bookingId', bookingId)}
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Back
                                            </Button>
                                        </AdminFormButtons>
                                    </form>
                                )}
                            </Form>
                        </AdminFormWrapper>
                    </AdminContentWrapper>
                )
            }
        </AdminPage>
    );
};

export default Showing;
